import { useState, useEffect, useRef } from "react";
import { Loader2, Check } from "lucide-react";
import { toast } from "react-toastify";
import {
  getNoteFromFirebase,
  saveNoteToFirebase,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { newmessagesuccess } from "../../utils/helper";

// Permanent Notes section for a deal, with @-mention autocomplete and
// notifications to mentioned users on save.
// Component name kept ("StickyNote") for backward import compatibility.
export default function StickyNote({ deal }) {
  const [{ user, users }] = useStateValue();

  const [text, setText] = useState("");
  const [lastSavedText, setLastSavedText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // @-mention state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  // Users we've already notified for this note (across saves) so we don't ping
  // them again every time the note is edited.
  const [notifiedUserIds, setNotifiedUserIds] = useState(new Set());
  const textareaRef = useRef(null);
  const justSavedTimerRef = useRef(null);

  useEffect(() => {
    async function fetchNote() {
      setLoading(true);
      const savedText =
        deal.notes ?? (await getNoteFromFirebase(String(deal.createdat)));
      const initial = savedText || "";
      setText(initial);
      setLastSavedText(initial);
      // Pre-populate notified set from existing mentions so we don't re-notify
      // the same people on first save.
      setNotifiedUserIds(extractMentionedIds(initial, users));
      setLoading(false);
    }
    if (deal?.createdat) fetchNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.createdat]);

  const getFullName = (u) =>
    `${u?.firstname || ""} ${u?.lastname || ""}`.trim();

  // Find all users currently @-mentioned in the given text.
  function extractMentionedIds(noteText, userList) {
    const ids = new Set();
    (userList || []).forEach((u) => {
      const name = getFullName(u);
      if (!name) return;
      const re = new RegExp(`@${escapeRegExp(name)}(?![\\w])`, "g");
      if (re.test(noteText)) ids.add(u.id);
    });
    return ids;
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    const before = value.slice(0, cursorPos);
    const match = before.match(/@([\w\s]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const filteredUsers = (users || []).filter((u) => {
    const fullName = getFullName(u).toLowerCase();
    return (
      u?.id !== user?.id &&
      u?.type !== 1 &&
      u?.type !== 2 &&
      (mentionQuery === "" || fullName.includes(mentionQuery.toLowerCase()))
    );
  });

  const handleSelectMention = (userObj) => {
    const fullName = getFullName(userObj);
    const before = text.slice(0, cursorPosition);
    const after = text.slice(cursorPosition);
    const newBefore = before.replace(/@([\w\s]*)$/, `@${fullName} `);
    const newText = newBefore + after;
    setText(newText);
    setShowMentions(false);
    // Restore caret position right after the inserted mention.
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) {
        const pos = newBefore.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
        setCursorPosition(pos);
      }
    });
  };

  const persist = async (textToSave) => {
    if (!deal?.createdat) return;
    if (textToSave === lastSavedText) return; // nothing to save
    setSaving(true);
    try {
      await saveNoteToFirebase(String(deal.createdat), textToSave);
      setLastSavedText(textToSave);
      setJustSaved(true);
      if (justSavedTimerRef.current) clearTimeout(justSavedTimerRef.current);
      justSavedTimerRef.current = setTimeout(() => setJustSaved(false), 2000);

      // Notify newly mentioned users (not previously notified).
      const currentIds = extractMentionedIds(textToSave, users);
      const toNotify = [...currentIds].filter(
        (id) => !notifiedUserIds.has(id),
      );
      if (toNotify.length > 0) {
        const subject =
          deal?.propertyaddress ||
          (deal?.dealtype ? `${deal.dealtype} deal` : "Deal");
        const senderName = getFullName(user) || "Someone";
        const dealRef = String(deal?.createdat || deal?.id || "");
        await Promise.all(
          toNotify.map((id) =>
            newmessagesuccess(
              "You were mentioned",
              id,
              `${senderName} mentioned you in notes on ${subject}`,
              dealRef,
              "mention",
              dealRef,
            ).catch((err) =>
              console.error("Mention notification failed:", err),
            ),
          ),
        );
        setNotifiedUserIds(new Set([...notifiedUserIds, ...toNotify]));
      }
    } catch (err) {
      console.error("Note save failed:", err);
      toast.error("Couldn't save note. Check your connection.");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save 1.5s after typing stops. Means changes persist without needing
  // the user to blur or close the drawer.
  useEffect(() => {
    if (loading) return;
    if (text === lastSavedText) return;
    const timer = setTimeout(() => {
      persist(text);
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, loading]);

  const handleBlur = () => {
    // Fire an immediate save on blur so closing the drawer doesn't lose the
    // last few seconds of typing. Skip while the mention dropdown is open
    // (user is about to click an item).
    setTimeout(() => {
      if (showMentions) return;
      persist(text);
    }, 120);
  };

  return (
    <section className="w-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Notes
        </h3>
        {saving ? (
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <Loader2 size={11} className="animate-spin" />
            Saving…
          </span>
        ) : justSaved ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-600">
            <Check size={11} />
            Saved
          </span>
        ) : null}
      </div>
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={loading ? "Loading..." : "Add notes — type @ to mention a teammate…"}
          disabled={loading}
          className="w-full min-h-[140px] border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y disabled:bg-slate-50"
        />
        {showMentions && filteredUsers.length > 0 && (
          <div
            className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto z-30"
            onMouseDown={(e) => e.preventDefault() /* keep textarea focused */}
          >
            {filteredUsers.map((u) => (
              <button
                type="button"
                key={u.id}
                onClick={() => handleSelectMention(u)}
                className="w-full text-left p-2 hover:bg-slate-50 flex items-center gap-2"
              >
                <img
                  src={u.profilepicture || "/assets/user.png"}
                  alt=""
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="text-sm text-slate-800">{getFullName(u)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
